import {CalendarIcon, XIcon} from "lucide-react";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {cn} from "@/lib/utils";
import {useStoredCompany} from "@/src/modules/company/store";
import {moment} from "@/src/modules/core/lib";

const EMPTY_RANGE = {from: undefined, to: undefined};

/**
 * @typedef {Object} DatePickerWithRangeProps
 * @property {{from?: Date | null; to?: Date | null}} [date]
 * @property {import("react").ComponentProps<typeof Calendar>["disabled"]} [disabled]
 * @property {(range: {from?: Date; to?: Date}) => void} onChange
 * @property {() => void} [onClear]
 * @property {boolean} [showClear]
 * @property {boolean} [completeRangeOnClose]
 * @property {boolean} [embedded]
 * @property {boolean} [disableFutureDates]
 */

/**
 * @param {DatePickerWithRangeProps} props
 */
export function DatePickerWithRange({
  date,
  disabled,
  onChange,
  onClear,
  showClear = false,
  /**
   * Safelisting-style: first click is a valid one-day range (coverage works immediately).
   * Calendar stays open so a second click on another day can extend the range.
   * Closing the popover finishes selection; re-clicking the same day does not clear it.
   */
  completeRangeOnClose = false,
  /** Flush inside a parent bordered group (safelisting date field). */
  embedded = false,
  disableFutureDates = false,
}) {
  const {t} = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [from, setFrom] = useState(date?.from ?? null);
  const [to, setTo] = useState(date?.to ?? null);
  const company = useStoredCompany();
  const timezone = useMemo(() => {
    return company?.timezone;
  }, [company]);

  useEffect(() => {
    setFrom(date?.from ?? null);
    setTo(date?.to ?? null);
  }, [date?.from, date?.to]);

  const hasSelection = Boolean(from || to);

  const calendarDisabled = useMemo(() => {
    if (disabled != null) return disabled;
    if (disableFutureDates) {
      return (day) => moment(day).isAfter(moment(), "day");
    }
    return undefined;
  }, [disabled, disableFutureDates]);

  const handleDateChange = (selected) => {
    if (!selected?.from) {
      if (
        completeRangeOnClose &&
        from &&
        to &&
        moment(from).isSame(to, "day")
      ) {
        return;
      }
      if (completeRangeOnClose && from && !to) {
        setTo(from);
        onChange({from, to: from});
        return;
      }
      setFrom(null);
      setTo(null);
      onChange(EMPTY_RANGE);
      return;
    }

    const nextFrom = selected.from;
    const nextTo = selected.to;
    const sameDayInPayload =
      nextTo != null && moment(nextFrom).isSame(nextTo, "day");

    if (!nextTo || sameDayInPayload) {
      setFrom(nextFrom);
      if (completeRangeOnClose) {
        setTo(nextFrom);
        onChange({from: nextFrom, to: nextFrom});
      } else {
        setTo(null);
        onChange({from: nextFrom, to: undefined});
      }
      return;
    }

    setFrom(nextFrom);
    setTo(nextTo);
    onChange({from: nextFrom, to: nextTo});
    setIsOpen(false);
  };

  const handleClear = () => {
    setFrom(null);
    setTo(null);
    setIsOpen(false);
    if (onClear) {
      onClear();
      return;
    }
    onChange(EMPTY_RANGE);
  };

  const handleOpenChange = (open) => {
    if (!open && completeRangeOnClose && from && !to) {
      setTo(from);
      onChange({from, to: from});
    }
    setIsOpen(open);
  };

  return (
    <div className={cn("flex items-stretch", embedded ? "gap-0" : "items-start gap-2")}>
      <div className="min-w-0 flex-1">
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger
            render={
              <Button
                id="date"
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-start border-border text-left font-normal",
                  embedded && "h-10 rounded-none border-0 shadow-none",
                  !from && !to && "text-muted-foreground",
                )}
              />
            }
          >
            {from ? (
              to ? (
                moment(from).isSame(to, "day") ? (
                  moment(from).format("MMM DD, YYYY")
                ) : (
                  <>
                    {moment(from).format("MMM DD, YYYY")} - {moment(to).format("MMM DD, YYYY")}
                  </>
                )
              ) : (
                moment(from).format("MMM DD, YYYY")
              )
            ) : (
              <span>{t("select_date_range")}</span>
            )}
            <CalendarIcon className="ml-auto size-4" />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              ISOWeek
              disabled={calendarDisabled}
              timeZone={timezone}
              defaultMonth={from ?? undefined}
              selected={{from: from ?? undefined, to: to ?? undefined}}
              onSelect={handleDateChange}
              numberOfMonths={1}
            />
          </PopoverContent>
        </Popover>
      </div>
      {showClear && hasSelection ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            "shrink-0",
            embedded && "h-10 rounded-none border-0 border-l shadow-none",
          )}
          aria-label={t("clear")}
          onClick={handleClear}
        >
          <XIcon className="size-4" />
          {t("clear")}
        </Button>
      ) : null}
    </div>
  );
}
